import traverse from 'traverse';
import {createCssVar} from './createCSSVar';

type NestedObject = {
  [key: string]: string | number | NestedObject;
};
type SetTokens = Record<string, string>;

type TransformObject<Obj extends NestedObject> = {
  [key in keyof Obj]: Obj[key] extends string | number
    ? string
    : Obj[key] extends NestedObject
      ? TransformObject<Obj[key]>
      : never;
};

/**
 * Converts the values in a deeply nested object to be CSS variable tokens & setters based on their path.
 * Optionally adds a prefix to the CSS variable name.
 *
 * ## Example
 *
 * ```js
 * const obj = {
 *   a: 'something',
 *   b: {
 *     c: 'something else'
 *   }
 * }
 *
 * const cssVars = generateTokensFromObject(obj);
 * ```
 *
 * ### Returns
 * ```js
 * // cssVars.tokens
 * {
 *  a: 'var(--a)',
 *  b: {
 *   c: 'var(--b-c)'
 *  }
 * }
 *
 * // cssVars.setTokens
 * {
 *  '--a': 'something',
 *  '--b-c-': 'something else'
 * }
 * ```
 */
export const generateTokensFromObject = <Obj extends NestedObject>(
  obj: Obj,
  options: {
    prefix?: string;
    applyNamespace?: boolean;
  } = {}
) => {
  const {prefix, applyNamespace} = options;
  const setTokens: SetTokens = {};

  const tokens: TransformObject<Obj> = traverse(obj).map(function (value) {
    if (this.isLeaf) {
      const varName = createCssVar({
        value: this.path.join('-'),
        prefix,
        applyNamespace,
      });

      setTokens[varName] = value;

      this.update(`var(${varName})`);
    }
  });

  return {
    tokens,
    setTokens,
  };
};
